import { useCallback, useEffect, useMemo, useState } from "react";
import { Card, SelectInput, TextInput, useEntityList } from "@pericles/framework-frontend";

import {
  createRoadmapScoringFactor,
  deleteRoadmapScoringEntry,
  deleteRoadmapScoringFactor,
  fetchRoadmapData,
  fetchRoadmapMembers,
  fetchRoadmapProjects,
  fetchRoadmapScoringEntries,
  fetchRoadmapScoringFactors,
  fetchRoadmapScoringRoles,
  fetchRoadmapSegments,
  isMigrationsRequiredError,
  saveRoadmapScoringEntry,
  updateRoadmapScoringFactor,
  updateRoadmapScoringRoleMembers,
  type RoadmapApplicationDetails,
  type RoadmapProcessRecord,
  type RoadmapProcessUseCaseLink,
  type RoadmapProject,
  type RoadmapScoringEntry,
  type RoadmapScoringFactor,
  type RoadmapScoringRole,
  type RoadmapSegmentRecord,
  type RoadmapUseCaseRecord,
  type RoadmapMigrationsRequiredDetails,
} from "../api/roadmapClient";

import { Badge, EmptyState, Pane, PaneHeader, Tabs } from "./roadmap/DesignSystem";
import { AtlasMigrationsRequiredCallout } from "./roadmap/AtlasMigrationsRequiredCallout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui";

type CustomerListItem = { id: string | number; name?: string };

type ScoreDrafts = Record<string, string>;

const DEFAULT_NEW_FACTOR = { factor: "", weight: "", owner: "", category: "" };

function buildScoreKey(segmentId: string, factorId: string): string {
  return `${segmentId}:${factorId}`;
}

function normalizeScoringRoleKey(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

function resolveScoringRoleMeta(owner?: string | null): { key: string; displayName: string } {
  const displayName = owner?.trim() || "Unassigned";
  const key = displayName === "Unassigned" ? "unassigned" : normalizeScoringRoleKey(displayName);
  return { key, displayName };
}

function resolveApplicability(raw?: Record<string, unknown> | null): string | null {
  if (!raw) return null;
  const entry = Object.entries(raw).find(([key]) => key.toLowerCase().includes("applicability"));
  const value = entry?.[1];
  if (typeof value === "string") {
    return value.trim() || null;
  }
  return null;
}

function formatApplicationName(segment: RoadmapSegmentRecord, fallbackIndex: number): string {
  if (segment.name) return segment.name;
  return `Application ${fallbackIndex + 1}`;
}

export function AiRoadmapScoringScreen(): JSX.Element {
  const [projects, setProjects] = useState<RoadmapProject[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(() => {
    return localStorage.getItem("studio.aiRoadmap.activeCustomer") ?? "";
  });
  const [selectedProjectId, setSelectedProjectId] = useState<string>(() => {
    return localStorage.getItem("studio.aiRoadmap.activeProject") ?? "";
  });
  const [scoringRoles, setScoringRoles] = useState<RoadmapScoringRole[]>([]);
  const [selectedRoleKey, setSelectedRoleKey] = useState<string>("");
  const [segments, setSegments] = useState<RoadmapSegmentRecord[]>([]);
  const [factors, setFactors] = useState<RoadmapScoringFactor[]>([]);
  const [entries, setEntries] = useState<RoadmapScoringEntry[]>([]);
  const [processes, setProcesses] = useState<RoadmapProcessRecord[]>([]);
  const [useCases, setUseCases] = useState<RoadmapUseCaseRecord[]>([]);
  const [processLinks, setProcessLinks] = useState<RoadmapProcessUseCaseLink[]>([]);
  const [membersUsers, setMembersUsers] = useState<Array<{ id: string; email: string }>>([]);
  const [roleMembersDraft, setRoleMembersDraft] = useState<Record<string, Set<string>>>({});
  const [loading, setLoading] = useState(false);
  const [dataError, setDataError] = useState<string | null>(null);
  const [factorDrafts, setFactorDrafts] = useState<Record<string, RoadmapScoringFactor>>({});
  const [newFactor, setNewFactor] = useState(DEFAULT_NEW_FACTOR);
  const [scoreDrafts, setScoreDrafts] = useState<ScoreDrafts>({});
  const [scoreError, setScoreError] = useState<string | null>(null);
  const [migrationError, setMigrationError] = useState<RoadmapMigrationsRequiredDetails | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [detailsSegment, setDetailsSegment] = useState<RoadmapSegmentRecord | null>(null);
  const [detailsData, setDetailsData] = useState<RoadmapApplicationDetails | null>(null);

  const { data: customerOptionsData } = useEntityList<CustomerListItem>({
    entity: "Customer",
    page: 0,
    pageSize: 1000,
  });

  const customerOptions = useMemo(() => {
    const options = (customerOptionsData ?? []).map((customer) => ({
      value: String(customer.id),
      label: customer.name ?? String(customer.id),
    }));
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [customerOptionsData]);

  const projectOptions = useMemo(() => {
    return projects.map((project) => ({ value: project.id, label: project.name }));
  }, [projects]);

  const roleTabs = useMemo(
    () => scoringRoles.map((role) => ({ id: role.key, label: role.displayName })),
    [scoringRoles],
  );

  const selectedScoringRole = useMemo(
    () => scoringRoles.find((role) => role.key === selectedRoleKey) ?? null,
    [scoringRoles, selectedRoleKey],
  );

  const entriesByKey = useMemo(() => {
    const rows = entries.filter((entry) => entry.scoringRole === selectedRoleKey && entry.segmentId);
    return new Map(rows.map((entry) => [buildScoreKey(entry.segmentId as string, entry.factorId), entry]));
  }, [entries, selectedRoleKey]);

  const factorsByRole = useMemo(() => {
    if (!selectedRoleKey) return [];
    return factors.filter((factor) => resolveScoringRoleMeta(factor.owner).key === selectedRoleKey);
  }, [factors, selectedRoleKey]);

  const factorsByRoleKey = useMemo(() => {
    const map = new Map<string, RoadmapScoringFactor[]>();
    factors.forEach((factor) => {
      const key = resolveScoringRoleMeta(factor.owner).key;
      const list = map.get(key) ?? [];
      list.push(factor);
      map.set(key, list);
    });
    return map;
  }, [factors]);

  const useCaseNameById = useMemo(() => {
    return new Map(useCases.map((useCase) => [useCase.id, useCase.name ?? useCase.id]));
  }, [useCases]);

  const processNameById = useMemo(() => {
    return new Map(processes.map((process) => [process.id, process.name ?? process.id]));
  }, [processes]);

  const processIdsByUseCase = useMemo(() => {
    const map = new Map<string, string[]>();
    processLinks.forEach((link) => {
      const list = map.get(link.useCaseId) ?? [];
      list.push(link.processId);
      map.set(link.useCaseId, list);
    });
    return map;
  }, [processLinks]);

  useEffect(() => {
    if (!selectedCustomerId) {
      setProjects([]);
      return;
    }
    fetchRoadmapProjects(selectedCustomerId)
      .then((data) => setProjects(data))
      .catch(() => setProjects([]));
  }, [selectedCustomerId]);

  useEffect(() => {
    if (!selectedProjectId) {
      setScoringRoles([]);
      setSegments([]);
      setFactors([]);
      setEntries([]);
      setMembersUsers([]);
      setRoleMembersDraft({});
      return;
    }

    localStorage.setItem("studio.aiRoadmap.activeProject", selectedProjectId);
    window.dispatchEvent(new Event("roadmap:project-changed"));

    const loadData = async () => {
      setLoading(true);
      setDataError(null);
      setMigrationError(null);
      try {
        const [segmentData, factorData, entryData, projectData, rolesData] = await Promise.all([
          fetchRoadmapSegments(selectedProjectId),
          fetchRoadmapScoringFactors(selectedProjectId),
          fetchRoadmapScoringEntries(selectedProjectId, undefined, { targetType: "SEGMENT" }),
          fetchRoadmapData(selectedProjectId),
          fetchRoadmapScoringRoles(selectedProjectId),
        ]);
        const membersData = await fetchRoadmapMembers(selectedProjectId).catch(() => null);
        setSegments(segmentData);
        setFactors(factorData);
        setEntries(entryData);
        setScoringRoles(rolesData);
        setMembersUsers(
          membersData?.users?.map((user) => ({ id: user.id, email: user.email })) ?? [],
        );
        setRoleMembersDraft(
          Object.fromEntries(
            rolesData.map((role) => [role.key, new Set(role.memberUserIds ?? [])]),
          ),
        );
        setProcesses(projectData.processes ?? []);
        setUseCases(projectData.useCases ?? []);
        setProcessLinks(projectData.links ?? []);
      } catch (error) {
        if (isMigrationsRequiredError(error)) {
          setMigrationError(error.details);
          setDataError(null);
        } else {
          const message = error instanceof Error ? error.message : "Impossible de charger le scoring.";
          setDataError(message);
        }
        setSegments([]);
        setFactors([]);
        setEntries([]);
        setProcesses([]);
        setUseCases([]);
        setProcessLinks([]);
        setScoringRoles([]);
        setMembersUsers([]);
        setRoleMembersDraft({});
      } finally {
        setLoading(false);
      }
    };

    void loadData();
  }, [selectedProjectId]);

  useEffect(() => {
    if (!selectedCustomerId) return;
    localStorage.setItem("studio.aiRoadmap.activeCustomer", selectedCustomerId);
  }, [selectedCustomerId]);

  useEffect(() => {
    if (!scoringRoles.length) return;
    if (scoringRoles.some((role) => role.key === selectedRoleKey)) return;
    setSelectedRoleKey(scoringRoles[0].key);
  }, [scoringRoles, selectedRoleKey]);

  useEffect(() => {
    setScoreDrafts({});
  }, [selectedRoleKey]);

  useEffect(() => {
    if (!factors.length) {
      setFactorDrafts({});
      return;
    }
    const nextDrafts = factors.reduce<Record<string, RoadmapScoringFactor>>((acc, factor) => {
      acc[factor.id] = { ...factor };
      return acc;
    }, {});
    setFactorDrafts(nextDrafts);
  }, [factors]);

  const refreshEntries = useCallback(async () => {
    if (!selectedProjectId) return;
    try {
      const entryData = await fetchRoadmapScoringEntries(selectedProjectId, undefined, { targetType: "SEGMENT" });
      setEntries(entryData);
    } catch (error) {
      if (isMigrationsRequiredError(error)) {
        setMigrationError(error.details);
        setScoreError(null);
        return;
      }
      const message = error instanceof Error ? error.message : "Impossible de recharger le scoring.";
      setScoreError(message);
    }
  }, [selectedProjectId]);

  const handleOpenDetails = useCallback(async (segment: RoadmapSegmentRecord) => {
    setDetailsSegment(segment);
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetailsError(null);
    try {
      setDetailsData(segment.detailsJson ?? null);
    } catch (error) {
      console.error(error);
      setDetailsError("Impossible de charger les détails d'application.");
      setDetailsData(null);
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  const handleCreateFactor = useCallback(async () => {
    if (!selectedProjectId) return;
    const weightValue = Number(newFactor.weight);
    if (!newFactor.factor.trim() || !Number.isFinite(weightValue)) {
      setDataError("Renseignez un facteur et un poids valides.");
      return;
    }
    setDataError(null);
    try {
      const created = await createRoadmapScoringFactor(selectedProjectId, {
        factor: newFactor.factor.trim(),
        weight: weightValue,
        owner: newFactor.owner.trim() || null,
        category: newFactor.category.trim() || null,
      });
      setFactors((current) => [...current, created]);
      setNewFactor(DEFAULT_NEW_FACTOR);
    } catch (error) {
      if (isMigrationsRequiredError(error)) {
        setMigrationError(error.details);
        return;
      }
      throw error;
    }
  }, [newFactor, selectedProjectId]);

  const handleUpdateFactor = useCallback(
    async (factorId: string) => {
      const draft = factorDrafts[factorId];
      if (!draft) return;
      const weightValue = Number(draft.weight);
      if (!draft.factor.trim() || !Number.isFinite(weightValue)) {
        setDataError("Renseignez un facteur et un poids valides.");
        return;
      }
      try {
        const updated = await updateRoadmapScoringFactor(factorId, {
          factor: draft.factor.trim(),
          weight: weightValue,
          owner: draft.owner?.trim() || null,
          category: draft.category?.trim() || null,
        });
        setFactors((current) => current.map((item) => (item.id === updated.id ? updated : item)));
      } catch (error) {
        if (isMigrationsRequiredError(error)) {
          setMigrationError(error.details);
          return;
        }
        throw error;
      }
    },
    [factorDrafts],
  );

  const handleDeleteFactor = useCallback(async (factorId: string) => {
    try {
      await deleteRoadmapScoringFactor(factorId);
      setFactors((current) => current.filter((item) => item.id !== factorId));
      setEntries((current) => current.filter((entry) => entry.factorId !== factorId));
    } catch (error) {
      if (isMigrationsRequiredError(error)) {
        setMigrationError(error.details);
        return;
      }
      throw error;
    }
  }, []);

  const handleScoreBlur = useCallback(
    async (segmentId: string, factorId: string) => {
      if (!selectedProjectId) return;
      if (selectedScoringRole && !selectedScoringRole.userCanScore) {
        setScoreError("Vous n'êtes pas autorisé à scorer pour ce rôle.");
        return;
      }
      const key = buildScoreKey(segmentId, factorId);
      const rawValue = (scoreDrafts[key] ?? "").trim();
      const existing = entriesByKey.get(key);

      if (!rawValue) {
        if (existing) {
          try {
            await deleteRoadmapScoringEntry(existing.id);
            setEntries((current) => current.filter((entry) => entry.id !== existing.id));
          } catch (error) {
            if (isMigrationsRequiredError(error)) {
              setMigrationError(error.details);
              return;
            }
            throw error;
          }
        }
        return;
      }

      const parsed = Number(rawValue);
      if (!Number.isFinite(parsed)) {
        setScoreError("Le score doit être un nombre.");
        return;
      }

      setScoreError(null);
      try {
        const saved = await saveRoadmapScoringEntry({
          projectId: selectedProjectId,
          targetType: "SEGMENT",
          segmentId,
          factorId,
          scoringRole: selectedRoleKey,
          score: parsed,
        });
        setEntries((current) => {
          const existingEntry = current.find((entry) => entry.id === saved.id);
          if (existingEntry) {
            return current.map((entry) => (entry.id === saved.id ? saved : entry));
          }
          return [...current, saved];
        });
        setScoreDrafts((current) => ({ ...current, [key]: String(saved.score ?? "") }));
      } catch (error) {
        if (isMigrationsRequiredError(error)) {
          setMigrationError(error.details);
          return;
        }
        throw error;
      }
    },
    [entriesByKey, scoreDrafts, selectedProjectId, selectedRoleKey, selectedScoringRole],
  );

  const selectedRoleMembers = useMemo(() => {
    return roleMembersDraft[selectedRoleKey] ?? new Set<string>();
  }, [roleMembersDraft, selectedRoleKey]);

  const handleToggleRoleMember = useCallback((userId: string) => {
    setRoleMembersDraft((current) => {
      const existing = new Set(current[selectedRoleKey] ?? []);
      if (existing.has(userId)) {
        existing.delete(userId);
      } else {
        existing.add(userId);
      }
      return { ...current, [selectedRoleKey]: existing };
    });
  }, [selectedRoleKey]);

  const handleSaveRoleMembers = useCallback(async () => {
    if (!selectedProjectId || !selectedRoleKey) return;
    const nextMembers = Array.from(selectedRoleMembers);
    try {
      const members = await updateRoadmapScoringRoleMembers(selectedProjectId, selectedRoleKey, nextMembers);
      setScoringRoles((current) =>
        current.map((role) =>
          role.key === selectedRoleKey
            ? { ...role, memberCount: members.length, memberUserIds: members }
            : role,
        ),
      );
      setRoleMembersDraft((current) => ({ ...current, [selectedRoleKey]: new Set(members) }));
    } catch (error) {
      console.error(error);
      setScoreError("Impossible de mettre à jour les membres du rôle.");
    }
  }, [selectedProjectId, selectedRoleKey, selectedRoleMembers]);

  const completionByRole = useMemo(() => {
    if (!segments.length || !factors.length) return [];
    const entryKeySet = new Set(
      entries
        .filter((entry) => entry.segmentId && entry.score != null && entry.scoringRole)
        .map((entry) => `${entry.scoringRole}:${entry.segmentId}:${entry.factorId}`),
    );
    return scoringRoles.map((role) => {
      const roleFactors = factorsByRoleKey.get(role.key) ?? [];
      let completeCount = 0;
      let pendingCount = 0;
      segments.forEach((segment) => {
        const isComplete = roleFactors.every((factor) =>
          entryKeySet.has(`${role.key}:${segment.id}:${factor.id}`),
        );
        if (isComplete) {
          completeCount += 1;
        } else {
          pendingCount += 1;
        }
      });
      return { role, completeCount, pendingCount };
    });
  }, [entries, factors, factorsByRoleKey, scoringRoles, segments]);

  const scoringTable = useMemo(() => {
    if (!segments.length || !factorsByRole.length) return null;
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
              <th className="px-3 py-2">Application</th>
              <th className="px-3 py-2">Use cases</th>
              {factorsByRole.map((factor) => (
                <th key={factor.id} className="px-3 py-2">
                  <div className="flex flex-col gap-1">
                    <span>{factor.factor}</span>
                    <span className="text-[10px] text-muted-foreground">Poids: {factor.weight}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {segments.map((segment, index) => (
              <tr key={segment.id} className="border-b border-border last:border-b-0">
                <td className="px-3 py-2 text-foreground">
                  <button
                    type="button"
                    className="text-left font-medium text-primary hover:underline"
                    onClick={() => void handleOpenDetails(segment)}
                  >
                    {formatApplicationName(segment, index)}
                  </button>
                </td>
                <td className="px-3 py-2 text-xs text-muted-foreground">
                  {segment.useCaseIds?.length ?? 0}
                </td>
                {factorsByRole.map((factor) => {
                  const key = buildScoreKey(segment.id, factor.id);
                  const entry = entriesByKey.get(key);
                  const value = scoreDrafts[key] ?? (entry?.score != null ? String(entry.score) : "");
                  return (
                    <td key={factor.id} className="px-3 py-2">
                      <input
                        type="number"
                        className="w-20 rounded-md border border-border bg-background px-2 py-1 text-xs text-foreground focus:border-primary focus:outline-none"
                        value={value}
                        onChange={(event) =>
                          setScoreDrafts((current) => ({ ...current, [key]: event.target.value }))
                        }
                        onBlur={() => void handleScoreBlur(segment.id, factor.id)}
                        disabled={selectedScoringRole ? !selectedScoringRole.userCanScore : false}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }, [
    entriesByKey,
    factorsByRole,
    handleOpenDetails,
    handleScoreBlur,
    scoreDrafts,
    segments,
    selectedScoringRole,
  ]);

  const detailsUseCases = useMemo(() => {
    if (!detailsSegment) return [];
    return (detailsSegment.useCaseIds ?? []).map((useCaseId) => ({
      id: useCaseId,
      name: useCaseNameById.get(useCaseId) ?? useCaseId,
      owner: useCases.find((useCase) => useCase.id === useCaseId)?.reviewOwner ?? null,
      applicability: resolveApplicability(
        useCases.find((useCase) => useCase.id === useCaseId)?.raw ?? null,
      ),
      processId: (processIdsByUseCase.get(useCaseId) ?? [])[0] ?? null,
    }));
  }, [detailsSegment, processIdsByUseCase, useCaseNameById, useCases]);

  const detailsProcesses = useMemo(() => {
    if (!detailsSegment) return [];
    const processIds = new Set<string>();
    (detailsSegment.useCaseIds ?? []).forEach((useCaseId) => {
      (processIdsByUseCase.get(useCaseId) ?? []).forEach((processId) => processIds.add(processId));
    });
    return Array.from(processIds).map((processId) => ({
      id: processId,
      name: processNameById.get(processId) ?? processId,
    }));
  }, [detailsSegment, processIdsByUseCase, processNameById]);

  const detailsStopReason = detailsSegment?.detailsStopReason ?? detailsData?.stopReason ?? null;
  const detailsMissingInfo = detailsSegment?.detailsMissingInfo ?? detailsData?.missingInfo ?? [];
  const detailsGeneratedAt = detailsSegment?.detailsGeneratedAt ?? null;
  const detailsInputsHash = detailsSegment?.detailsInputsHash ?? null;

  return (
    <div className="space-y-6">
      <Card className="flex flex-wrap gap-4">
        <div className="min-w-[220px]">
          <div className="text-xs font-semibold uppercase text-muted-foreground">Client</div>
          <SelectInput
            value={selectedCustomerId}
            onChange={(event) => {
              setSelectedCustomerId(event.target.value);
              setSelectedProjectId("");
            }}
          >
            <option value="">Sélectionner</option>
            {customerOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </SelectInput>
        </div>
        <div className="min-w-[220px]">
          <div className="text-xs font-semibold uppercase text-muted-foreground">Projet de roadmap</div>
          <SelectInput
            value={selectedProjectId}
            onChange={(event) => setSelectedProjectId(event.target.value)}
          >
            <option value="">Sélectionner</option>
            {projectOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </SelectInput>
        </div>
      </Card>

      {dataError ? (
        <Card className="border border-red-200 bg-red-50 text-sm text-red-700">{dataError}</Card>
      ) : null}

      {!selectedProjectId ? (
        <Card>
          <EmptyState title="Choisissez un projet pour gérer le scoring." />
        </Card>
      ) : migrationError ? (
        <Card className="space-y-4 p-4">
          <AtlasMigrationsRequiredCallout details={migrationError} />
          <EmptyState
            title="Migrations nécessaires"
            description="Appliquez les migrations Prisma pour activer le scoring Atlas."
          />
        </Card>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
          <Pane className="min-h-[320px]">
            <PaneHeader title="Facteurs" meta="Définissez les facteurs, poids et responsables." />
            <div className="space-y-4 px-4 py-4">
              <div className="space-y-3 rounded-lg border border-dashed border-border p-3">
                <div className="text-xs font-semibold uppercase text-muted-foreground">Ajouter un facteur</div>
                <div className="grid gap-3">
                  <TextInput
                    value={newFactor.factor}
                    placeholder="Facteur"
                    onChange={(event) =>
                      setNewFactor((current) => ({ ...current, factor: event.target.value }))
                    }
                  />
                  <TextInput
                    type="number"
                    value={newFactor.weight}
                    placeholder="Poids"
                    onChange={(event) =>
                      setNewFactor((current) => ({ ...current, weight: event.target.value }))
                    }
                  />
                  <TextInput
                    value={newFactor.owner}
                    placeholder="Owner"
                    onChange={(event) =>
                      setNewFactor((current) => ({ ...current, owner: event.target.value }))
                    }
                  />
                  <TextInput
                    value={newFactor.category}
                    placeholder="Catégorie"
                    onChange={(event) =>
                      setNewFactor((current) => ({ ...current, category: event.target.value }))
                    }
                  />
                  <button
                    type="button"
                    className="rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground"
                    onClick={() => void handleCreateFactor()}
                  >
                    Ajouter
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="text-xs text-muted-foreground">Chargement...</div>
              ) : factors.length ? (
                <div className="space-y-3">
                  {factors.map((factor) => {
                    const draft = factorDrafts[factor.id] ?? factor;
                    return (
                      <div key={factor.id} className="rounded-lg border border-border p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="text-sm font-semibold text-foreground">{factor.factor}</div>
                          <Badge label={`Poids ${factor.weight}`} />
                        </div>
                        <div className="mt-3 grid gap-3">
                          <TextInput
                            value={draft.factor}
                            placeholder="Facteur"
                            onChange={(event) =>
                              setFactorDrafts((current) => ({
                                ...current,
                                [factor.id]: { ...draft, factor: event.target.value },
                              }))
                            }
                          />
                          <TextInput
                            type="number"
                            value={String(draft.weight ?? "")}
                            placeholder="Poids"
                            onChange={(event) =>
                              setFactorDrafts((current) => ({
                                ...current,
                                [factor.id]: { ...draft, weight: Number(event.target.value) },
                              }))
                            }
                          />
                          <TextInput
                            value={draft.owner ?? ""}
                            placeholder="Owner"
                            onChange={(event) =>
                              setFactorDrafts((current) => ({
                                ...current,
                                [factor.id]: { ...draft, owner: event.target.value },
                              }))
                            }
                          />
                          <TextInput
                            value={draft.category ?? ""}
                            placeholder="Catégorie"
                            onChange={(event) =>
                              setFactorDrafts((current) => ({
                                ...current,
                                [factor.id]: { ...draft, category: event.target.value },
                              }))
                            }
                          />
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              className="rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground"
                              onClick={() => void handleUpdateFactor(factor.id)}
                            >
                              Mettre à jour
                            </button>
                            <button
                              type="button"
                              className="rounded-md border border-border px-3 py-2 text-xs font-semibold text-muted-foreground"
                              onClick={() => void handleDeleteFactor(factor.id)}
                            >
                              Supprimer
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">Aucun facteur pour le moment.</div>
              )}
            </div>
          </Pane>

          <Pane className="min-h-[320px]">
            <PaneHeader
              title="Scoring par rôle"
              meta="Évaluez les applications pour le rôle sélectionné."
              actions={
                roleTabs.length ? (
                  <Tabs
                    tabs={roleTabs}
                    activeId={selectedRoleKey}
                    onChange={(value) => setSelectedRoleKey(value as string)}
                  />
                ) : null
              }
            />
            <div className="space-y-3 px-4 py-4">
              {scoreError ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  {scoreError}
                </div>
              ) : null}
              {selectedScoringRole && !selectedScoringRole.userCanScore ? (
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                  Accès en lecture seule pour ce rôle.
                </div>
              ) : null}
              {completionByRole.length ? (
                <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                  {completionByRole.map((item) => (
                    <Badge
                      key={item.role.key}
                      label={
                        item.pendingCount
                          ? `${item.role.displayName}: ${item.pendingCount} en attente`
                          : `${item.role.displayName}: complet`
                      }
                    />
                  ))}
                </div>
              ) : null}
              {loading ? (
                <div className="text-xs text-muted-foreground">Chargement...</div>
              ) : !factorsByRole.length ? (
                <EmptyState title="Ajoutez des facteurs pour saisir les scores." />
              ) : !segments.length ? (
                <EmptyState title="Aucune application disponible." />
              ) : (
                scoringTable
              )}
              {membersUsers.length ? (
                <div className="rounded border border-slate-200 p-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="text-xs font-semibold uppercase text-muted-foreground">Membres du rôle</div>
                    <button
                      type="button"
                      className="text-xs font-semibold text-primary"
                      onClick={() => void handleSaveRoleMembers()}
                      disabled={!selectedRoleKey}
                    >
                      Enregistrer
                    </button>
                  </div>
                  <div className="mt-2 grid gap-2">
                    {membersUsers.map((user) => (
                      <label key={user.id} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <input
                          type="checkbox"
                          checked={selectedRoleMembers.has(user.id)}
                          onChange={() => handleToggleRoleMember(user.id)}
                          disabled={!selectedRoleKey}
                        />
                        <span>{user.email}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ) : null}
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Scores enregistrés automatiquement au changement.</span>
                <button
                  type="button"
                  className="text-xs font-semibold text-primary"
                  onClick={() => void refreshEntries()}
                >
                  Rafraîchir
                </button>
              </div>
            </div>
          </Pane>
        </div>
      )}

      <Dialog open={detailsOpen} onOpenChange={(open) => setDetailsOpen(open)}>
        <DialogContent className="max-h-[90vh] w-full max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detailsSegment?.name ?? "Application details"}</DialogTitle>
          </DialogHeader>
          {detailsError ? (
            <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {detailsError}
            </div>
          ) : null}
          {detailsLoading ? (
            <div className="text-xs text-muted-foreground">Chargement…</div>
          ) : (
            <div className="space-y-4 text-sm text-foreground">
              <div className="rounded border border-slate-200 p-3">
                <div className="text-xs font-semibold uppercase text-muted-foreground">Application core</div>
                <div className="mt-2 grid gap-3 md:grid-cols-2">
                  <div>
                    <div className="text-xs font-semibold uppercase text-muted-foreground">Purpose</div>
                    <div>{detailsSegment?.purpose ?? "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-muted-foreground">Solution type</div>
                    <div>{detailsSegment?.solutionType ?? "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-muted-foreground">AI what it does</div>
                    <div>{detailsSegment?.aiWhatItDoes ?? "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-muted-foreground">Impact</div>
                    <div>{detailsSegment?.impact ?? "—"}</div>
                  </div>
                </div>
              </div>

              <div className="rounded border border-slate-200 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-xs font-semibold uppercase text-muted-foreground">Generation status</div>
                  {detailsStopReason ? <Badge label={detailsStopReason} /> : null}
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  {detailsGeneratedAt ? `Généré le ${detailsGeneratedAt}` : "Aucun détail généré."}
                </div>
                {detailsMissingInfo.length ? (
                  <div className="mt-2">
                    <div className="text-xs font-semibold uppercase text-muted-foreground">Missing info</div>
                    <ul className="mt-1 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
                      {detailsMissingInfo.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {detailsInputsHash ? (
                  <div className="mt-2 text-xs text-muted-foreground">Inputs hash: {detailsInputsHash}</div>
                ) : null}
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <div className="text-xs font-semibold uppercase text-muted-foreground">Related processes</div>
                  <ul className="mt-2 space-y-2 text-xs text-muted-foreground">
                    {detailsProcesses.length ? (
                      detailsProcesses.map((process) => (
                        <li key={process.id} className="flex items-center justify-between gap-2">
                          <span>{process.name}</span>
                          <a
                            className="text-primary hover:underline"
                            href={`/ai_roadmap/review?processId=${process.id}`}
                          >
                            Open
                          </a>
                        </li>
                      ))
                    ) : (
                      <li>—</li>
                    )}
                  </ul>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase text-muted-foreground">Related use cases</div>
                  <ul className="mt-2 space-y-2 text-xs text-muted-foreground">
                    {detailsUseCases.length ? (
                      detailsUseCases.map((useCase) => (
                        <li key={useCase.id} className="space-y-1">
                          <div className="flex items-center justify-between gap-2">
                            <span>{useCase.name}</span>
                            <a
                              className="text-primary hover:underline"
                              href={`/ai_roadmap/review?useCaseId=${useCase.id}${
                                useCase.processId ? `&processId=${useCase.processId}` : ""
                              }`}
                            >
                              Open
                            </a>
                          </div>
                          <div className="flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                            {useCase.owner ? <Badge label={useCase.owner} /> : null}
                            {useCase.applicability ? <Badge label={useCase.applicability} /> : null}
                          </div>
                        </li>
                      ))
                    ) : (
                      <li>—</li>
                    )}
                  </ul>
                </div>
              </div>

              {detailsData ? (
                <div className="space-y-4 rounded border border-slate-200 p-3">
                  <div>
                    <div className="text-xs font-semibold uppercase text-muted-foreground">Overview</div>
                    <div>{detailsData.structured.execSummary || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-muted-foreground">Purpose</div>
                    <div>{detailsSegment?.purpose || "—"}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-muted-foreground">Scope</div>
                    <div className="text-xs text-muted-foreground">In scope</div>
                    <ul className="mt-1 list-disc space-y-1 pl-5">
                      {detailsData.structured.scope.in.length ? (
                        detailsData.structured.scope.in.map((item) => <li key={item}>{item}</li>)
                      ) : (
                        <li>—</li>
                      )}
                    </ul>
                    <div className="mt-2 text-xs text-muted-foreground">Out of scope</div>
                    <ul className="mt-1 list-disc space-y-1 pl-5">
                      {detailsData.structured.scope.out.length ? (
                        detailsData.structured.scope.out.map((item) => <li key={item}>{item}</li>)
                      ) : (
                        <li>—</li>
                      )}
                    </ul>
                    <div className="mt-2 text-xs text-muted-foreground">Prerequisites</div>
                    <ul className="mt-1 list-disc space-y-1 pl-5">
                      {detailsData.structured.prereqs.length ? (
                        detailsData.structured.prereqs.map((item) => <li key={item}>{item}</li>)
                      ) : (
                        <li>—</li>
                      )}
                    </ul>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-muted-foreground">AI architecture</div>
                    <div className="text-xs text-muted-foreground">Recommended approach</div>
                    <div>{detailsData.structured.aiArchitecture.recommendedApproach || "—"}</div>
                    <div className="mt-2 text-xs text-muted-foreground">Tooling</div>
                    <ul className="mt-1 list-disc space-y-1 pl-5">
                      {detailsData.structured.aiArchitecture.tooling.length ? (
                        detailsData.structured.aiArchitecture.tooling.map((item) => <li key={item}>{item}</li>)
                      ) : (
                        <li>—</li>
                      )}
                    </ul>
                    <div className="mt-2 text-xs text-muted-foreground">Non-goals</div>
                    <ul className="mt-1 list-disc space-y-1 pl-5">
                      {detailsData.structured.aiArchitecture.nonGoals.length ? (
                        detailsData.structured.aiArchitecture.nonGoals.map((item) => <li key={item}>{item}</li>)
                      ) : (
                        <li>—</li>
                      )}
                    </ul>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-muted-foreground">Data & integrations</div>
                    <div className="text-xs text-muted-foreground">Data sources</div>
                    <ul className="mt-1 list-disc space-y-1 pl-5">
                      {detailsData.structured.dataAndIntegrations.dataSources.length ? (
                        detailsData.structured.dataAndIntegrations.dataSources.map((item) => <li key={item}>{item}</li>)
                      ) : (
                        <li>—</li>
                      )}
                    </ul>
                    <div className="mt-2 text-xs text-muted-foreground">Integrations</div>
                    <ul className="mt-1 list-disc space-y-1 pl-5">
                      {detailsData.structured.dataAndIntegrations.integrations.length ? (
                        detailsData.structured.dataAndIntegrations.integrations.map((item) => <li key={item}>{item}</li>)
                      ) : (
                        <li>—</li>
                      )}
                    </ul>
                    <div className="mt-2 text-xs text-muted-foreground">Gaps</div>
                    <ul className="mt-1 list-disc space-y-1 pl-5">
                      {detailsData.structured.dataAndIntegrations.gaps.length ? (
                        detailsData.structured.dataAndIntegrations.gaps.map((item) => <li key={item}>{item}</li>)
                      ) : (
                        <li>—</li>
                      )}
                    </ul>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-muted-foreground">Libraries to build</div>
                    <ul className="mt-1 space-y-2">
                      {detailsData.structured.librariesToBuild.length ? (
                        detailsData.structured.librariesToBuild.map((lib) => (
                          <li key={lib} className="rounded border border-slate-200 p-2 text-xs">
                            {lib}
                          </li>
                        ))
                      ) : (
                        <li>—</li>
                      )}
                    </ul>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-muted-foreground">Delivery plan</div>
                    <ul className="mt-1 space-y-2">
                      {detailsData.structured.deliveryPlan.length ? (
                        detailsData.structured.deliveryPlan.map((phase) => (
                          <li key={phase.milestone} className="rounded border border-slate-200 p-2 text-xs">
                            <div className="font-semibold">{phase.milestone}</div>
                            <div className="text-muted-foreground">{phase.what || "—"}</div>
                            {phase.risks.length ? (
                              <div className="text-muted-foreground">Risks: {phase.risks.join(", ")}</div>
                            ) : null}
                          </li>
                        ))
                      ) : (
                        <li>—</li>
                      )}
                    </ul>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase text-muted-foreground">Value hypothesis</div>
                    <div className="text-xs text-muted-foreground">Value</div>
                    <div>{detailsData.structured.valueHypothesis.value || "—"}</div>
                    <div className="mt-2 text-xs text-muted-foreground">Confidence</div>
                    <div>{detailsData.structured.valueHypothesis.confidence}</div>
                    <div className="mt-2 text-xs text-muted-foreground">Why</div>
                    <div>{detailsData.structured.valueHypothesis.why || "—"}</div>
                  </div>
                  {detailsData.structured.evidence.useCaseIds.length ||
                  detailsData.structured.evidence.processIds.length ? (
                    <div>
                      <div className="text-xs font-semibold uppercase text-muted-foreground">Evidence refs</div>
                      <ul className="mt-1 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
                        {detailsData.structured.evidence.useCaseIds.map((id) => (
                          <li key={`usecase-${id}`}>Use case {id}</li>
                        ))}
                        {detailsData.structured.evidence.processIds.map((id) => (
                          <li key={`process-${id}`}>Process {id}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">Aucun détail d'application enregistré.</div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
